import { apiErrorFrom, networkError } from "@/shared/api/errors";
import { apiUrl, authHeaders, type QueryValue } from "@/shared/api/http";

/* Calls that return files (offer PDFs, Excel exports and templates) and uploads that report
   their progress. */

function filenameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  const plain = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  return plain ?? fallback;
}

async function fetchFile(
  path: string,
  query?: Readonly<Record<string, QueryValue>>,
): Promise<{ blob: Blob; filename: string }> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path, query), { headers: authHeaders() });
  } catch {
    throw networkError();
  }
  if (!response.ok) {
    throw apiErrorFrom(await response.json().catch(() => null), response.status, response.headers);
  }
  const fallback = path.split("/").filter(Boolean).pop() ?? "download";
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition"), fallback),
  };
}

/** Download a file through the session's token; resolves to the saved file name. */
export async function downloadFile(
  path: string,
  query?: Readonly<Record<string, QueryValue>>,
): Promise<string> {
  const { blob, filename } = await fetchFile(path, query);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
  return filename;
}

/**
 * Open a file (an offer PDF) in a new tab. The tab opens during the click, so pop-up blockers
 * allow it, and closes again if the download fails.
 */
export async function openFile(path: string): Promise<void> {
  const opened = window.open("", "_blank");
  try {
    const { blob } = await fetchFile(path);
    const url = URL.createObjectURL(blob);
    if (opened) opened.location.href = url;
    else window.location.href = url;
  } catch (error) {
    opened?.close();
    throw error;
  }
}

/**
 * Send a multipart body with upload progress (fetch cannot report it). Resolves to the parsed
 * JSON body; rejects with an ApiError like `request`.
 */
export function uploadWithProgress(
  path: string,
  body: FormData,
  onProgress: (fraction: number) => void,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiUrl(path));
    for (const [name, value] of Object.entries(authHeaders())) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => {
      reject(networkError());
    };
    xhr.onload = () => {
      let payload: unknown;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload);
      else reject(apiErrorFrom(payload, xhr.status));
    };
    xhr.send(body);
  });
}

const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.85;

/**
 * Shrink a photo before upload so it stays under PHP's upload limits and uploads fast on mobile
 * data: longest edge 2048px, JPEG. Small files and non-images pass through unchanged.
 */
export async function downscaleImage(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.size < 600_000) {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
    });
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
  } catch {
    return file;
  }
}
